import type { IntlShape } from "react-intl";
import type { UiMatchMode } from "@/components/match/match-mode";

export const MATCH_RESULT_PDF_SECTIONS = [
  "summary",
  "team",
  "agents",
  "requirements",
  "powers",
  "powerDetail",
  "ranking",
  "dataQuality",
  "notes",
  "personDetails",
  "matrices",
] as const;

export type MatchResultPdfSection = (typeof MATCH_RESULT_PDF_SECTIONS)[number];

export type MatchResultPdfSectionSettings = Record<MatchResultPdfSection, boolean>;

// Zusammensetzungs-Auflage einer Anforderungszeile ("min. 4 im Team"), als {id, values} statt
// fertigem Text: der Renderer ist ein eigenes React-Bundle MIT IntlProvider, aber OHNE Apollo.
// Alles, was nur Uebersetzung braucht, reist als id; nur was Referenzdaten braucht, reist als
// fertiger String (siehe MatchResultPdfRequirementRow.label).
export interface MatchResultPdfMessageRef {
  id: string;
  values: Record<string, number>;
}

export interface MatchResultPdfRequirementRow {
  // Vorformatiert, weil labelForCondition Referenzdaten aus Apollo braucht (Abteilungen,
  // Laender, Branchen) - die im Renderer-Bundle nicht existieren.
  label: string;
  isMust: boolean;
  met: number;
  teamSize: number;
  close: number;
  composition: MatchResultPdfMessageRef | null;
  // Gesetzt nur bei fulfilled && met === 1: die Anforderung ruht auf genau dieser Person.
  soleCarrierName: string | null;
}

export interface MatchResultPdfPerson {
  id: string;
  name: string;
  profession?: string | null;
  // Nur befuellt, wenn personDetails aktiv ist - sonst bleibt die Nutzlast schlank.
  detail?: MatchResultPdfPersonDetail | null;
}

export interface MatchResultPdfPersonDetail {
  // Vorformatierter Befundsatz ("Erfuellt keine Anforderung - der Beitrag liegt in den Powers").
  verdict: string | null;
  met: number;
  total: number;
  teamMedianMet: number | null;
  criteria: Array<{
    label: string;
    isMust: boolean;
    state: "met" | "partial" | "open" | "unrated";
  }>;
  hiddenCriteria: number;
  clusters: Array<{
    key: string;
    score: number | null;
    strong: number;
    assessed: number;
  }>;
  powerFit: number | null;
  tilts: MatchResultPdfTilt[];
  anchors: string[];
  soleAnchors: string[];
}

// Neigung einer Achse. side/grade/bothWeak reichen dem Renderer, um denselben Verdikt-Text
// zu bauen, den die UI zeigt - die Message-ids sind in beiden Buendeln dieselben.
export interface MatchResultPdfTilt {
  axis: "DRIVE" | "MINDSET";
  side: "PUSH" | "PULL" | "EXPLOIT" | "EXPLORE" | null;
  grade: "LEANS" | "CLEAR" | "STRONG" | null;
  bothWeak: boolean;
}

export interface MatchResultPdfTeam {
  teamId: string;
  persons: MatchResultPdfPerson[];
  factSkillPercent: number | null;
  powersPercent: number | null;
  powerGroups: Array<{
    key: string;
    percent: number | null;
    assessedMembers: number;
    // Zielniveau aus dem Powers-Wunschprofil; null, wenn kein Profil gesetzt ist.
    target: number | null;
  }>;
  criteriaCount: number;
  measuredPowerMembers: number;
  totalPowerMembers: number;
  mustMet: number;
  mustTotal: number;
  kannMet: number;
  kannTotal: number;
  soleRequirementsCount: number;
  strengths: MatchResultPdfRequirementRow[];
  gaps: MatchResultPdfRequirementRow[];
  // Fachkompetenzen, die das Team stark mitbringt, ohne dass jemand danach gefragt hat.
  extraSkills: string[];
  tilts: MatchResultPdfTilt[];
  clusterCarriers: Array<{ key: string; carriers: number; signature: number; assessed: number }>;
  boosterCoverage: Array<{ element: string; covered: number; assessed: number }>;
}

export interface MatchResultPdfAgent {
  id: string;
  name: string;
  role: string;
  covers: string[];
}

export interface MatchResultPdfRequirementMatrix {
  columns: Array<{ label: string; fullLabel: string; isMust: boolean }>;
  rows: Array<{ personId: string; personName: string; cells: boolean[] }>;
}

export interface MatchResultPdfPowersMatrix {
  columns: Array<{ element: string; code: string; group: string }>;
  rows: Array<{ personId: string; personName: string; cells: Array<number | null> }>;
}


export interface MatchResultPdfPayload {
  fileName: string;
  locale: string;
  mode: UiMatchMode;
  matchTitle: string;
  projectName?: string | null;
  resultName?: string | null;
  resultCreatedAt?: string | null;
  exportedAt: string;
  candidateCount?: number | null;
  requirementsCount: number;
  unknownRequirementsCount: number;
  dataQualityNotes: string[];
  notes?: string | null;
  sections: MatchResultPdfSectionSettings;
  teams: MatchResultPdfTeam[];
  agents: MatchResultPdfAgent[];
  requirementMatrix: MatchResultPdfRequirementMatrix | null;
  powersMatrix: MatchResultPdfPowersMatrix | null;
  // Nur im Rangliste-Modus gesetzt; dort bleibt `teams` leer, weil kein Team existiert.
  rank: MatchResultPdfRank | null;
}

export const DEFAULT_MATCH_RESULT_PDF_SECTIONS: MatchResultPdfSectionSettings = {
  summary: true,
  team: true,
  agents: true,
  requirements: true,
  powers: true,
  powerDetail: false,
  ranking: true,
  dataQuality: true,
  notes: false,
  personDetails: false,
  matrices: false,
};

// ASSESS und OPTIMIZE rendern in der App dieselbe Team-Dashboard-Oberflaeche (resultSurface in
// ResultStep) - deshalb tragen sie hier dieselben Abschnitte. Vorher fehlte ASSESS "powers",
// obwohl die Powers-Boxen auf dem Schirm standen.
export function sectionsForMode(mode: UiMatchMode): MatchResultPdfSection[] {
  // Beide Ranglisten tragen denselben Bericht und damit dieselben Abschnitte: die Zahlen kommen
  // aus buildMatchResultPdfRank bzw. buildMatchResultPdfRankForCompanies, nicht aus der
  // Team-Anforderungsanalyse. "summary" heisst hier die Match-Analyse des Pools
  // (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3). Was Unternehmen nicht
  // haben - Powers, KI-Eignung, Umkreis -, laesst der Renderer weg, weil die Werte fehlen; eine
  // eigene Abschnittsliste dafuer waere eine zweite Stelle, an der die beiden Seiten driften.
  if (mode === "RANK" || mode === "RANK_COMPANIES") {
    return ["summary", "ranking", "requirements", "personDetails", "matrices", "notes"];
  }
  // Personen-Details und Matrizen stehen am Ende: die beiden schweren Zusatzbloecke, beide
  // standardmaessig aus.
  return [
    "summary",
    "team",
    "agents",
    "powers",
    "powerDetail",
    "requirements",
    "dataQuality",
    "notes",
    "personDetails",
    "matrices",
  ];
}

// Export-Knopf und Schalterliste lesen dieselbe Quelle: ein Modus ohne Abschnitte hat kein
// Dokument und darf folglich auch keinen Knopf zeigen.
export function matchResultPdfSupportsMode(mode: UiMatchMode): boolean {
  return sectionsForMode(mode).length > 0;
}

export function effectiveSectionsForMode(
  mode: UiMatchMode,
  sections: MatchResultPdfSectionSettings,
): MatchResultPdfSectionSettings {
  const allowed = new Set(sectionsForMode(mode));
  return Object.fromEntries(
    Object.keys(DEFAULT_MATCH_RESULT_PDF_SECTIONS).map((section) => [
      section,
      allowed.has(section as MatchResultPdfSection) && sections[section as MatchResultPdfSection],
    ]),
  ) as MatchResultPdfSectionSettings;
}

// ─── Rangliste: kandidaten-eigener Bericht ────────────────────────────────────
//
// Ein Rangliste-Ergebnis ist kandidaten-, nicht team-foermig. Die Zahlen hier stammen aus
// buildRankingInsights / buildCandidateRanking - denselben Funktionen, die den Bildschirm
// speisen -, nie aus buildRequirementAnalysis / buildFactSkillFulfillment: die sind auf
// Team-Komposition zugeschnitten und lesen die Composition-Operatoren eines RANK-Designs als
// Absicht, obwohl der Composition-Editor in diesem Modus gar nicht sichtbar ist.

export interface MatchResultPdfRankMessageRef {
  id: string;
  values: Record<string, string | number>;
}

export interface MatchResultPdfRankCriterionRow {
  label: string;
  isMust: boolean;
  met: number;
  total: number;
  // Nicht bewertbare Items (classification UNKNOWN): weder erfuellt noch gescheitert.
  unknown: number;
  // Davon per KI-Begruendung erfuellt - dieselbe Zusatzzeile wie auf dem Schirm.
  aiMet: number;
  /** met / total, 0..1. Treibt den Anteilsbalken. */
  share: number;
  /** 1 - mittlere Erfuellung, 0..1. Nur bei Engpaessen gesetzt. */
  gap: number | null;
  // Vorformatiert, weil die Verteilungszeile Referenzdaten und Zahlenformate braucht.
  distribution: string | null;
}

export interface MatchResultPdfRankCriterionState {
  label: string;
  isMust: boolean;
  // "lenient" ist kein Zwischenzustand von met, sondern eine eigene Aussage: vorhanden, Stufe
  // nicht belegt. "unknown" heisst nicht bewertbar, nicht "nicht erfuellt".
  state: "met" | "lenient" | "unknown" | "unmet";
  actual: string | null;
  aiReason: string | null;
}

export interface MatchResultPdfRankCandidateDetail {
  /** 0..100 */
  suitability: number | null;
  suitabilityRatingId: string | null;
  positive: string[];
  negative: string[];
  fazit: string | null;
  questions: string[];
  musts: Array<{ label: string; state: MatchResultPdfRankCriterionState["state"] }>;
  criteria: MatchResultPdfRankCriterionState[];
}

export interface MatchResultPdfRankCandidate {
  teamFit?: number | null;
  personId: string;
  name: string;
  // Die Zeile unter dem Namen: bei Personen der Beruf, bei Unternehmen der Inaktiv-Hinweis.
  // Bewusst nicht "profession" - dasselbe Feld traegt auf beiden Seiten eine andere Angabe.
  subtitle: string | null;
  band: "A" | "B" | "C" | "REVIEW" | "UNQUALIFIED";
  /** Ganzheitliche KI-Eignung, 0..100. */
  smyclesScore: number | null;
  /** Anforderungs-Fit (Kann), 0..100. */
  requirementFit: number | null;
  /** 0..100 */
  powerFit: number | null;
  mustMet: number;
  mustTotal: number;
  // Summe der Erfuellungsgrade, kein Zaehler: genesis liefert graduelle Werte.
  kannSum: number;
  // Nur die bewertbaren Kann-Kriterien ...
  kannTotal: number;
  // ... von allen definierten. Die Differenz ist die Belastbarkeit des Scores und wird
  // getrennt ausgewiesen, nie einmultipliziert.
  kannDefined: number;
  // Der konkrete Pruefgrund, nicht bloss "zu pruefen". Nur im REVIEW-Band gesetzt.
  reviewReasonId: string | null;
  // Ob dieser Kandidat im Dokument steht. `candidates` traegt IMMER die ganze Rangliste, damit
  // der Listenindex die Pool-Position bleibt: eine vorgefilterte Liste wuerde die Auswahl
  // 4/9/17 als 1/2/3 drucken und dem Bildschirm widersprechen.
  included: boolean;
  detail: MatchResultPdfRankCandidateDetail | null;
}

export interface MatchResultPdfRank {
  // Eine Taxonomie, nicht zwei: die Baender partitionieren den Pool exakt (A+B+C+REVIEW+
  // UNQUALIFIED = total). "qualifiziert" zaehlt dagegen das Muss-Gate, und ein Kandidat, der
  // das Gate besteht, aber unter der C-Schwelle liegt, landet trotzdem im Band UNQUALIFIED -
  // beide Zahlen nebeneinander zu zeigen ergaebe mehr Kandidaten als es gibt.
  pool: {
    total: number;
    review: number;
    unqualified: number;
    bands: { A: number; B: number; C: number };
  };
  // Der Sichtzustand, mit dem exportiert wurde. Ohne ihn ist ein Bandbuchstabe auf Papier
  // bedeutungslos - und die Sortierachse veraendert die Baender selbst.
  thresholds: { a: number; b: number; c: number };
  sortBy: "smycles" | "requirements" | "power" | "team";
  teamFitBasis?: { members: Array<{ id: string; name: string }>; scorecard: { name: string } };
  recommendation: MatchResultPdfRankMessageRef | null;
  hardestMust: { label: string; failed: number; total: number; passPercent: number } | null;
  topFulfilled: MatchResultPdfRankCriterionRow[];
  bottlenecks: MatchResultPdfRankCriterionRow[];
  ai: { reasonCount: number };
  dataQuality: { missing: number; expected: number };
  radius: { city: string; radiusKm: number; inside: number; outside: number; missing: number } | null;
  // Die ganze Kriterienliste mit den Pool-Zahlen, die topFulfilled/bottlenecks nur fuer je drei
  // Zeilen zeigen. Ein Kriterium ohne Zahl ist auf Papier eine Ueberschrift ohne Aussage.
  requirements: MatchResultPdfRankCriterionRow[];
  candidates: MatchResultPdfRankCandidate[];
  // Die Kriterienmatrix reist in payload.requirementMatrix: derselbe Typ, derselbe Renderer.
  matrix: MatchResultPdfRequirementMatrix | null;
}

// NUR WinAnsi-Zeichen. react-pdf laeuft hier auf den eingebauten Helvetica-Schriften
// (siehe components/cv/templates/shared.ts) - die koennen Latin-1 und sonst nichts. Zeichen wie
// ✓ ● ◐ ○ ↔ kommen NICHT als Fehler zurueck, sie rendern als Muell (✓ wurde zu "Ê", ○ zu nichts).
// Wer hier ein Symbol ergaenzt, prueft es gegen WINANSI_SAFE im Test nebenan.
export const CRITERION_GLYPH: Record<string, string> = {
  met: "+",
  partial: "~",
  open: "–",
  unrated: "?",
};

// Dieselbe Beschraenkung, deshalb kein Haken: erfuellt/nicht erfuellt als Plus und Halbgeviertstrich.
export const MATRIX_GLYPH = { met: "+", unmet: "–" } as const;

// Vier Zustaende einer Kandidaten-Zeile. "kulant erfuellt" ist kein halbes "erfuellt", sondern
// eine eigene Aussage (vorhanden, Stufe nicht belegt) - deshalb ein eigenes Zeichen. WinAnsi,
// siehe oben.
export const RANK_CRITERION_GLYPH: Record<"met" | "lenient" | "unknown" | "unmet", string> = {
  met: "+",
  lenient: "~",
  unknown: "?",
  unmet: "–",
};

// A/B/C bleiben Buchstaben - sie sind nur zusammen mit den Schwellen lesbar, die sie erzeugt
// haben. Geteilt zwischen Dokument und Auswahlliste, damit beide dieselben Woerter benutzen wie
// der Bildschirm.
export function rankClusterLabel(cluster: string, intl: IntlShape): string {
  switch (cluster) {
    case "REVIEW":
      return intl.formatMessage({ id: "match.pdf.rankCluster.review", defaultMessage: "Review" });
    case "UNQUALIFIED":
      return intl.formatMessage({ id: "match.pdf.rankCluster.unqualified", defaultMessage: "Criteria not met" });
    default:
      return cluster;
  }
}
